// Dev-only fail-loud missing-message attribution.
//
// Under route-scoped (lazy) domain catalogs, a rendered message id whose
// owning domain's catalog is not loaded silently falls back to its raw id —
// the CXPPA-26 / #865344 incident class that static analysis alone cannot
// fully predict. In dev builds, this hook turns that silent fallback into an
// immediate, attributable console error: the missing id, the domain that owns
// it (via the generated owners manifest), and the remediation.
//
// Prod is a strict no-op: NODE_ENV === 'production' yields undefined, so the
// IntlProvider's default onError behavior is unchanged and this module is
// dropped from the prod chunk graph by dead-code elimination at the injection
// site (RPPCore.start's devIntlOnError pass-through).
//
// The owners manifest maps message-id PREFIXES to owning domains — first
// segments plus the shortest unique-owner prefixes under segments shared
// across domains (see the manifest builder). It is generated alongside
// domainCatalogDeps.json.
import { MissingTranslationError, type IntlShape } from 'react-intl'
import messageIdOwners from './__generated__/messageIdOwners.json'

interface MessageIdOwners {
  note: string
  /** id prefix -> owning domain id */
  prefixes: Record<string, string>
}

const OWNERS = messageIdOwners as unknown as MessageIdOwners

/** Map a message id to its owning domain via longest-prefix match. */
export const ownerOfMessageId = (id: string): string | undefined => {
  let best: string | undefined
  let bestLen = -1
  for (const [prefix, domain] of Object.entries(OWNERS.prefixes)) {
    if (prefix.length > bestLen && id.startsWith(prefix)) {
      best = domain
      bestLen = prefix.length
    }
  }
  return best
}

/** One error per unique id per session — a broken grid must not spam hundreds. */
const reported = new Set<string>()

/** The IntlProvider onError when dev; undefined in prod (no-op there). */
export const intlDevFailLoud: IntlShape['onError'] | undefined =
  process.env.NODE_ENV === 'production'
    ? undefined
    : (error): void => {
        // react-intl reports a missing id as MissingTranslationError
        // (descriptor.id carries the id).
        if (!(error instanceof MissingTranslationError)) return
        const id = error.descriptor?.id
        if (typeof id !== 'string' || reported.has(id)) return
        reported.add(id)
        const owner = ownerOfMessageId(id)
        // eslint-disable-next-line no-console
        console.error(
          `[intl] Raw message id rendered: "${id}".` +
            (owner
              ? ` Owning domain: "${owner}".`
              : ' Owner unknown (id not in any domain catalog).') +
            ` Its catalog was not loaded when the component rendered.` +
            ` Fix: regenerate apps/ipp/src/nav-labels/__generated__/domainCatalogDeps.json` +
            ` (UPDATE_DEPS=1) so the owning domain is in the rendering route's catalogDeps,` +
            ` or keep that domain's EN_US catalog eager. This error is dev-only.`
        )
      }
